import { Fragment } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { CCard } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilChevronRight } from "@coreui/icons";
import {
  TradingStrategySignalAction,
  type TradingStrategySignalDto,
} from "../../../api/hooks/useSignals";
import {
  calendarDaysAgo,
  formatDayHeading,
  formatSignalTime,
  formatTimeOfDay,
  isSameDay,
} from "../../../lib/dates";
import { heldFor } from "../../../lib/signal-copy";
import { signalDetailPath } from "../../../routes/route-paths";
import { SignalActionBadge } from "./SignalActionBadge";
import { useTradingStrategies } from "../../../api/hooks/useTradingStrategies";

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface SignalDay {
  key: string;
  label: string;
  /** Shown beside "Today" and "Yesterday", which don't say which day they are. */
  date: string | null;
  signals: TradingStrategySignalDto[];
}

/** Signals under the day they arrived, newest first, each saying what it did to its trade. */
export function SignalFeed({ signals }: { signals: TradingStrategySignalDto[] }) {
  const { t } = useTranslation("signals");
  const { t: tStrategies } = useTranslation("tradingStrategies");
  const { data: strategies } = useTradingStrategies();
  const days = groupByDay(signals, t);

  // Two sides of one strategy share its name, so the caption says which side signalled.
  const captionFor = (signal: TradingStrategySignalDto) => {
    const direction = strategies?.find((s) => s.id === signal.tradingStrategyId)?.direction;
    return direction ? `${signal.tradingStrategyName} · ${tStrategies(`sides.${direction}`)}` : signal.tradingStrategyName;
  };

  return (
    <CCard>
      <div className="list-group list-group-flush">
        {days.map((day) => (
          <Fragment key={day.key}>
            <div className="list-group-item bg-body-tertiary small fw-semibold d-flex justify-content-between gap-3">
              <span>{day.label}</span>
              {day.date && <span className="fw-normal text-body-secondary">{day.date}</span>}
            </div>
            {day.signals.map((signal) => (
              <SignalFeedRow key={signal.id} signal={signal} caption={captionFor(signal)} />
            ))}
          </Fragment>
        ))}
      </div>
    </CCard>
  );
}

/** The signals arrive newest first, so each day's run is contiguous. */
function groupByDay(signals: TradingStrategySignalDto[], t: Translate): SignalDay[] {
  const days: SignalDay[] = [];

  for (const signal of signals) {
    const key = new Date(signal.receivedAt).toDateString();
    const current = days[days.length - 1];
    if (current?.key === key) {
      current.signals.push(signal);
      continue;
    }

    const daysAgo = calendarDaysAgo(signal.receivedAt);
    const heading = formatDayHeading(signal.receivedAt);
    const label = daysAgo === 0 ? t("feed.today") : daysAgo === 1 ? t("feed.yesterday") : heading;
    days.push({ key, label, date: daysAgo <= 1 ? heading : null, signals: [signal] });
  }

  return days;
}

/** One signal: when, what, which strategy, and its trade. Opens its details page. */
function SignalFeedRow({ signal, caption }: { signal: TradingStrategySignalDto; caption: string }) {
  return (
    <Link
      to={signalDetailPath(signal.id)}
      className="list-group-item list-group-item-action d-flex flex-wrap align-items-center column-gap-3 row-gap-1 py-3"
    >
      <span className="font-monospace small" style={{ minWidth: "3.25rem" }}>
        {formatTimeOfDay(signal.receivedAt)}
      </span>
      <SignalActionBadge action={signal.action} />
      <span className="flex-grow-1" style={{ minWidth: 0 }}>
        <span className="fw-semibold d-block">{signal.symbol}</span>
        <span className="small d-block text-truncate text-body-secondary">{caption}</span>
      </span>
      <TradeSummary signal={signal} />
      <CIcon icon={cilChevronRight} size="sm" className="text-body-secondary" />
    </Link>
  );
}

function TradeSummary({ signal }: { signal: TradingStrategySignalDto }) {
  const { t } = useTranslation("signals");
  const trade = signal.trade;
  const strong = { strong: <strong className="fw-semibold text-body" /> };

  let summary: React.ReactNode = null;
  if (signal.action === TradingStrategySignalAction.Exit && trade.openedBy) {
    const held = heldFor(trade.openedBy.receivedAt, signal.receivedAt, t);
    summary = <Trans t={t} i18nKey="feed.closesHeld" values={{ held }} components={strong} />;
  } else if (signal.action === TradingStrategySignalAction.Exit && trade.openedBeforeSignalsStarted) {
    summary = t("feed.closesBeforeSignals");
  } else if (signal.action === TradingStrategySignalAction.Buy && trade.closedBy) {
    const closedAt = trade.closedBy.receivedAt;
    const when = isSameDay(closedAt, signal.receivedAt) ? formatTimeOfDay(closedAt) : formatSignalTime(closedAt);
    summary = <Trans t={t} i18nKey="feed.opensClosed" values={{ when }} components={strong} />;
  } else if (signal.action === TradingStrategySignalAction.Buy && trade.stillOpen) {
    summary = <Trans t={t} i18nKey="feed.opensStillOpen" components={strong} />;
  }

  if (summary === null) {
    return null;
  }

  return <span className="small text-body-secondary">{summary}</span>;
}
